import { useCallback, useEffect, useRef, useState } from 'react';
import {
  explainSpeechError,
  getSpeechRecognitionCtor,
  isSpeechRecognitionSupported,
  type SpeechStatus,
  type SpeechRecognitionInstance,
  type UseSpeechToText,
} from '@ai-tiaozs/shared';

/**
 * 浏览器端语音转文字 React hook
 * - isSupported=false 时,调用方应展示降级提示
 * - 录音中的 text 会实时更新(final + interim)
 * - 停止后,text 保留为最终结果;调用方把它塞到表单
 */
export function useSpeechToText(options: { lang?: string; interim?: boolean } = {}): UseSpeechToText {
  const lang = options.lang ?? 'zh-CN';
  const interim = options.interim ?? true;

  const isSupported = isSpeechRecognitionSupported();

  const [status, setStatus] = useState<SpeechStatus>(isSupported ? 'idle' : 'unsupported');
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const recRef = useRef<SpeechRecognitionInstance | null>(null);
  const finalTextRef = useRef('');

  const cleanup = useCallback(() => {
    const r = recRef.current;
    if (r) {
      try {
        r.abort();
      } catch {
        // ignore
      }
      r.onresult = null;
      r.onerror = null;
      r.onend = null;
      recRef.current = null;
    }
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const start = useCallback(() => {
    if (!isSupported) {
      setError('当前浏览器不支持语音识别,请用 Chrome/Edge 或直接输入文字');
      setStatus('unsupported');
      return;
    }
    if (status === 'recording') return;
    setError(null);
    finalTextRef.current = '';
    setText('');
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) return;
    const r = new Ctor();
    r.lang = lang;
    r.continuous = false;
    r.interimResults = interim;
    r.maxAlternatives = 1;
    r.onresult = (ev) => {
      let interimText = '';
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const result = ev.results[i];
        const transcript = result[0]?.transcript ?? '';
        if (result.isFinal) {
          finalTextRef.current += transcript;
        } else {
          interimText += transcript;
        }
      }
      setText((finalTextRef.current + interimText).trim());
    };
    r.onerror = (ev) => {
      setError(explainSpeechError(ev.error));
      setStatus('idle');
      cleanup();
    };
    r.onend = () => {
      setText((prev) => (finalTextRef.current || prev).trim());
      setStatus('idle');
    };
    recRef.current = r;
    try {
      r.start();
      setStatus('recording');
    } catch (e) {
      setError(e instanceof Error ? e.message : '启动录音失败');
      setStatus('idle');
      cleanup();
    }
  }, [isSupported, lang, interim, status, cleanup]);

  const stop = useCallback(() => {
    const r = recRef.current;
    if (r) {
      try {
        r.stop();
      } catch {
        // ignore
      }
    }
  }, []);

  const reset = useCallback(() => {
    finalTextRef.current = '';
    setText('');
    setError(null);
  }, []);

  return { status, text, error, start, stop, reset, isSupported };
}
